// Frontend mirror of functions-shared/utils/deriveDispatchPlan.js. Verified by test:dispatch-plan-mirror.
// functions/utils/deriveDispatchPlan.js
//
// Deterministic crew-dispatch plan (service_case_dispatch P2). Turns the operational fault
// classification + the extended engineering analysis into the concrete "who/where/what" a
// dispatcher needs — people, roof/house/ground access, equipment, and the CT action. This is
// RULES, not an LLM (Vesta writes the narrative/theories; this decides the crew logistics).
//
// Pure + defensive: any input may be missing. Returns a best-effort plan.
//
// dhOlReKP addendum (2026-09-09): the eleven service-case categories are the "why" list and each
// carries a crew profile. An operator's explicit pick (hints.explicitCategory) beats the live
// classification; without one the classification decides and the category / title fill the gaps.

// GSE policy is independent of equipment location: every job requires home access.
export const HOME_ACCESS_POLICY_REASON =
  'Home access required for every job by GSE policy, including ground-mounted systems and outdoor equipment.'

export function requireHomeAccess(plan = {}) {
  const reason = String(plan.houseReason || '').trim()
  return {
    ...plan,
    houseAccess: true,
    houseReason:
      plan.houseAccess && reason
        ? reason.includes(HOME_ACCESS_POLICY_REASON)
          ? reason
          : `${HOME_ACCESS_POLICY_REASON} ${reason}`
        : HOME_ACCESS_POLICY_REASON,
  }
}

export function applyEngineeringHomeAccessPolicy(analysis) {
  const access = analysis?.access || {}
  return {
    ...analysis,
    access: {
      ...access,
      indoor: true,
      indoorReason: HOME_ACCESS_POLICY_REASON,
      summary: [
        HOME_ACCESS_POLICY_REASON,
        access.roof ? 'Roof access required.' : '',
        access.groundEquipment ? 'Ground equipment access required.' : '',
        access.gated ? 'Arrange gate access.' : '',
      ]
        .filter(Boolean)
        .join(' '),
    },
  }
}

// Category -> issue kind. Keys are the eleven service-case categories, lower-cased, no spaces.
export const CATEGORY_KINDS = {
  inverter: { kind: 'inverter', category: 'Inverter', issueType: 'Inverter fault (component)' },
  monitoring: {
    kind: 'comms',
    category: 'Monitoring',
    issueType: 'Monitoring / comms — feed offline',
  },
  battery: { kind: 'battery', category: 'Battery', issueType: 'Battery / storage fault' },
  panel: { kind: 'panel', category: 'Panel', issueType: 'Panel / module fault' },
  'roof/leak': { kind: 'roof', category: 'Roof/Leak', issueType: 'Roof leak / penetration' },
  electrical: {
    kind: 'electrical',
    category: 'Electrical',
    issueType: 'Electrical / service panel',
  },
  'utility/pto': {
    kind: 'utility',
    category: 'Utility/PTO',
    issueType: 'Utility / PTO turn-on visit',
  },
  performance: { kind: 'production', category: 'Performance', issueType: 'Underperformance' },
  communication: {
    kind: 'comms',
    category: 'Communication',
    issueType: 'Communication / gateway offline',
  },
  hardware: {
    kind: 'hardware',
    category: 'Hardware',
    issueType: 'Hardware fault (optimizer, RSD, disconnect)',
  },
  other: { kind: 'other', category: 'Other', issueType: 'On-site diagnosis' },
}

const meterish = (hints = {}) =>
  String(hints.vestaIssueType || '').toLowerCase() === 'meter' ||
  /meter|rgm|\bct\b/.test(String(hints.title || '').toLowerCase())

// The profile for one of the eleven categories (null for anything else). A metering complaint
// filed under Performance keeps the meter profile (CT action, no roof).
export function kindForCategory(category, hints = {}) {
  const key = String(category || '')
    .toLowerCase()
    .replace(/\s+/g, '')
  const hit = CATEGORY_KINDS[key]
  if (!hit) return null
  if (hit.kind === 'production' && meterish(hints))
    return {
      kind: 'meter',
      category: 'Performance',
      issueType: 'Meter / CT — revenue-meter under-reading',
    }
  return { ...hit }
}

// Fault classification -> issue kind. Falls back to case category / vestaIssueType / title.
export function resolveKind(operational = {}, hints = {}) {
  // The operator picked the category on the case: their choice wins over the live classification.
  if (hints.explicitCategory) {
    const picked = kindForCategory(hints.explicitCategory, hints)
    if (picked) return picked
  }

  const cls = String(operational.classification || '').toLowerCase()
  if (/sustained-underperformance|production-drop/.test(cls))
    return {
      kind: 'production',
      category: 'Performance',
      issueType: 'Production — dead inverter or string(s)',
    }
  if (/inverter-dark/.test(cls))
    return {
      kind: 'dark',
      category: 'Inverter',
      issueType: 'System dark — inverter tripped / arc-fault',
    }
  if (/sync-gap|comms-dark/.test(cls))
    return { kind: 'comms', category: 'Monitoring', issueType: 'Monitoring / comms — feed offline' }
  if (/rgm|meter/.test(cls))
    return {
      kind: 'meter',
      category: 'Performance',
      issueType: 'Meter / CT — RGM low-read (production OK)',
    }

  // No live classification (e.g. an 'observe' site with a component case like Van's standalone inverter).
  const title = String(hints.title || '').toLowerCase()
  if (meterish(hints))
    return {
      kind: 'meter',
      category: 'Performance',
      issueType: 'Meter / CT — revenue-meter under-reading',
    }
  const byCategory = kindForCategory(hints.category, hints)
  if (byCategory && byCategory.kind !== 'other') return byCategory
  if (/offline|comms|gateway|no data/.test(title))
    return { kind: 'comms', category: 'Monitoring', issueType: 'Monitoring / comms — feed offline' }
  if (/powerwall|battery/.test(title))
    return { kind: 'battery', category: 'Battery', issueType: 'Battery / storage fault' }
  if (/inverter|blinking|standalone|arc|dark/.test(title))
    return { kind: 'inverter', category: 'Inverter', issueType: 'Inverter fault (component)' }
  // 'Other', or no category at all, with nothing in the title to go on: the generic profile.
  return byCategory || { ...CATEGORY_KINDS.other }
}

const isTesla = a =>
  Array.isArray(a?.inverterUnits) &&
  a.inverterUnits.some(u => /tesla/i.test(u?.manufacturer || u?.model || ''))
const mountOf = a => String(a?.mount?.type || '').toLowerCase()
const hasGroundEquip = a =>
  mountOf(a) === 'ground' ||
  mountOf(a) === 'mixed' ||
  (Array.isArray(a?.inverterUnits) &&
    a.inverterUnits.some(
      u => /ground|exterior/i.test(u?.location || '') || u?.mountedOn === 'ground array'
    ))
const mspIndoor = a => /indoor|basement/i.test(a?.electrical?.mainServicePanelLocation || '')
const invIndoor = a =>
  Array.isArray(a?.inverterUnits) &&
  a.inverterUnits.some(u => /garage|basement|indoor/i.test(u?.location || ''))
const batteryIndoor = a =>
  a?.battery?.present && /garage|basement|indoor/i.test(a?.battery?.location || '')
const spareInverter = a => {
  const u = Array.isArray(a?.inverterUnits) ? a.inverterUnits[0] : null
  return u?.model && !/not found|unknown/i.test(u.model)
    ? `Spare ${u.manufacturer || ''} ${u.model} inverter`.replace(/\s+/g, ' ').trim()
    : 'Spare inverter (match on site)'
}
function ladder(a) {
  const stories = String(a?.roof?.stories || '')
  const metal = /metal|standing.?seam/i.test(a?.roof?.material || '')
  const base = stories === '2' ? '2-story ladder + fall protection' : 'ladder'
  return metal ? `${base} (metal roof — anchors/S-5)` : base
}

// Metering action sentence from the electrical picture.
function ctAction(a) {
  const meter = String(a?.electrical?.productionMeter || '').toLowerCase()
  const inter = String(a?.electrical?.interconnectionType || '').toLowerCase()
  const ct =
    a?.electrical?.ctLocation && !/not found|unknown/i.test(a.electrical.ctLocation)
      ? a.electrical.ctLocation
      : null
  if (/inverter-internal/.test(meter))
    return `Check each inverter's internal metering + connectivity — one unit under-reporting reads as ~half the site. Field-verify CTs at each inverter${ct ? ` (${ct})` : ''} and the utility bi-di meter.`
  if (/line-side|supply/.test(inter))
    return `Verify CT orientation/scaling at the service-entrance metering — 1φ 3-wire, so check BOTH legs; likely a reversed/mis-scaled CT or a dropped leg${ct ? ` (${ct})` : ''}.`
  return `Re-seat/re-orient the CTs at the revenue meter first${ct ? ` — ${ct}` : ''}; if that reads right, check the panel's internal metering/comms.`
}

/**
 * @param {object} analysis  system.engineeringAnalysis (extended) — may be null
 * @param {object} operational  siteMonitoring.lifetimePerformance.operational — may be null
 * @param {object} hints  { category, explicitCategory, vestaIssueType, title, reportingParty }
 * @returns dispatchPlan
 */
export function deriveDispatchPlan(analysis = null, operational = null, hints = {}) {
  const a = analysis || null
  const op = operational || {}
  const { kind, category, issueType } = resolveKind(op, hints)
  const tesla = isTesla(a)
  const techWord = tesla ? 'Tesla-certified technician' : 'solar technician'
  const groundEquip = hasGroundEquip(a)
  const roofMount = mountOf(a) === 'roof' || mountOf(a) === 'mixed'

  let roofAccess = false,
    houseAccess = false,
    houseReason = null,
    groundAccess = groundEquip,
    groundReason = groundEquip ? 'Ground-mounted array / exterior equipment' : null
  let people = 1,
    roles = [`1 ${techWord}`],
    equipment = [],
    priority

  if (kind === 'meter') {
    roofAccess = false
    houseAccess = mspIndoor(a) || !!a?.access?.indoor
    houseReason = houseAccess
      ? `Metering / CTs at the ${a?.electrical?.mainServicePanelLocation || 'indoor'} service panel`
      : null
    const lineSide = /line-side|supply/i.test(a?.electrical?.interconnectionType || '')
    people = lineSide ? 2 : 1
    roles = lineSide ? [`1 ${techWord}`, '1 electrician'] : [`1 ${techWord}`]
    equipment = [
      'CT clamp meter',
      'Spare CTs',
      'Phasing tester',
      'Laptop (inverter/monitoring config)',
    ]
    if (lineSide) equipment.push('Both-leg voltage check')
  } else if (kind === 'dark' || kind === 'inverter') {
    roofAccess = roofMount
    houseAccess = invIndoor(a) || batteryIndoor(a) || mspIndoor(a)
    houseReason = houseAccess ? 'Inverter / battery / service panel is indoors' : null
    const dcFault = /ground fault|dc fault|arc/i.test(String(hints.title || ''))
    people = dcFault ? 2 : 1
    roles = dcFault
      ? [`1-2 ${techWord}s`]
      : [`1 ${techWord}`, '(electrician if disconnect/breaker work)']
    equipment = [
      'Laptop (inverter)',
      'Arc-fault / fault-code diagnostic',
      'Multimeter',
      spareInverter(a),
      'Spare RSD / DC connectors',
    ]
    if (/ground fault|dc fault/i.test(String(hints.title || '')))
      equipment.push('DC insulation tester / megger', 'String-level DVM')
    if (roofMount) equipment.push(ladder(a))
  } else if (kind === 'comms') {
    roofAccess = false
    houseAccess = true
    houseReason = 'Homeowner router / Wi-Fi (to re-provision the inverter) — often indoors'
    people = 1
    roles = [`1 ${techWord}`]
    equipment = [
      'Laptop',
      'Homeowner Wi-Fi credentials',
      'Spare gateway / cellular modem',
      'Cell-signal check',
    ]
  } else if (kind === 'production') {
    roofAccess = roofMount
    houseAccess = mspIndoor(a)
    houseReason = houseAccess ? 'Service panel / interconnection is indoors' : null
    const invCount = Array.isArray(a?.inverterUnits)
      ? a.inverterUnits.reduce((n, u) => n + (Number(u.quantity) || 1), 0)
      : 0
    const big =
      (Number(a?.systemSize?.dcKilowatts) || 0) > 20 ||
      invCount > 2 ||
      String(a?.roof?.stories) === '2'
    people = big ? 2 : 1
    roles = big ? [`1 ${techWord}`, '1 electrician'] : [`1 ${techWord}`]
    equipment = [
      spareInverter(a),
      'String fuses',
      'CT clamp / phasing meter',
      'Laptop (inverter config)',
    ]
    if (roofMount) equipment.push(ladder(a))
    equipment.push('Spare CTs')
  } else if (kind === 'battery') {
    roofAccess = false
    houseAccess = batteryIndoor(a) || true
    houseReason = 'Battery / storage is typically in the garage or basement'
    people = 1
    roles = [`1 ${techWord}`]
    equipment = ['Laptop (battery/inverter)', 'Fault-code diagnostic', 'Multimeter']
  } else if (kind === 'panel') {
    // A module swap: two on the roof, the replacement matched to what is up there.
    roofAccess = roofMount
    houseAccess = false
    houseReason = null
    people = 2
    roles = [`2 ${techWord}s`]
    equipment = [
      'Replacement module (match make and model on site)',
      'MC4 connectors + crimper',
      'IV curve tracer or string DVM',
      'Torque wrench (mid / end clamps)',
      'Fall protection',
    ]
    if (roofMount) equipment.push(ladder(a))
  } else if (kind === 'roof') {
    // A leak is on the roof and shows inside: roofer plus a technician to lift and reset modules.
    roofAccess = true
    houseAccess = true
    houseReason = 'Attic / ceiling inspection at the leak'
    people = 2
    roles = ['1 roofer', `1 ${techWord}`]
    equipment = [
      'Flashing kit + sealant (match roof material)',
      'Replacement lags / L-feet',
      'Moisture meter',
      'Tarp',
      ladder(a),
    ]
  } else if (kind === 'electrical') {
    roofAccess = false
    houseAccess = true
    houseReason = 'Main service panel / disconnects'
    people = 1
    roles = ['1 electrician']
    equipment = [
      'Multimeter',
      'Torque screwdriver',
      'Spare breakers (match panel make)',
      'Lockout / tagout kit',
      'Phasing tester',
    ]
  } else if (kind === 'utility') {
    // Permission to operate: the default is the turn-on visit (Erik, 2026-09-09: operators edit
    // DOWN for paperwork-only cases rather than up). One technician energizes and confirms monitoring.
    roofAccess = false
    houseAccess = mspIndoor(a)
    houseReason = houseAccess ? 'Main service panel / AC disconnect to energize the system' : null
    people = 1
    roles = [`1 ${techWord}`]
    equipment = [
      'Multimeter (verify AC voltage at the disconnect)',
      'Laptop / app (inverter commissioning + monitoring check)',
      'Lockout / tagout kit',
      'PTO letter / utility approval on hand',
      'Homeowner Wi-Fi credentials (monitoring)',
    ]
  } else if (kind === 'hardware') {
    roofAccess = roofMount
    houseAccess = invIndoor(a) || mspIndoor(a)
    houseReason = houseAccess ? 'Inverter / service panel is indoors' : null
    people = 1
    roles = [`1 ${techWord}`]
    equipment = [
      'Laptop (inverter / optimizer config)',
      'Multimeter',
      'Spare optimizer / RSD (match model)',
      'Spare DC connectors',
    ]
    if (roofMount) equipment.push(ladder(a))
  } else if (kind === 'other') {
    roofAccess = false
    houseAccess = false
    houseReason = null
    people = 1
    roles = [`1 ${techWord}`]
    equipment = ['Laptop', 'Multimeter', 'Basic hand tools']
  }

  // Priority suggestion from the operational lane + reporting party (operator can override on severity).
  const it = op.interventionType,
    st = op.status
  if (st === 'needs-intervention' && it === 'truck-roll') priority = 'High'
  else if (st === 'needs-intervention') priority = 'Medium'
  else if (st === 'observe') priority = 'Low'
  else priority = op.priority === 'high' ? 'High' : 'Medium'

  return requireHomeAccess({
    kind,
    issueCategory: category,
    issueType,
    people,
    roles,
    roofAccess,
    houseAccess,
    houseReason,
    groundAccess,
    groundReason,
    equipment,
    ctAction: kind === 'meter' ? ctAction(a) : 'N/A — not a metering issue',
    priority,
    derivedFrom: {
      classification: op.classification || null,
      kind,
      hadAnalysis: !!a,
      explicitCategory: hints.explicitCategory || null,
    },
  })
}
